/**
 * SystemsComparisonTable — the whole four-system ontology in one table,
 * rendered on the hub and on every spoke (08/21/2026 quality pass). One
 * glance — or one retrieval chunk — states what each system is, what it
 * names, its format, and where it lives. `current` highlights the row of
 * the page you are on; omit it on the hub.
 *
 * One semantic table and one copy of every definition. On phones its rows
 * become full-width cards; desktop retains the five-column comparison.
 *
 * WHY THE EXPLICIT TABLE ROLES (measured, not assumed — 09/14/2026).
 * The phone layout sets `display: block` / `grid` on the table, rows and
 * cells (systems-table.module.css), and Chromium then drops the native table
 * semantics. Read straight out of the accessibility tree of a probe page
 * (Workspace/visuals/table-semantics-probe-2026-09-14.html) rendered with
 * this exact CSS:
 *   - no ARIA roles at all: every row, header and cell is exposed as
 *     `generic` — the table is a bag of text;
 *   - roles on table, row and headers only: rows and headers survive, but
 *     each `td` is still exposed as `generic`;
 *   - the full set, `role="cell"` included: table > row > rowheader / cell,
 *     exactly the relationships the desktop table has.
 * So `role="cell"` on the data cells is load-bearing, and
 * tests/systems-comparison-accessibility.test.ts pins it. jsx-a11y's
 * `no-interactive-element-to-noninteractive-role` objects because it counts
 * `td` as an interactive element (its gridcell mapping); here the role
 * restates the cell's own native semantics that the CSS strips, so the rule
 * is suppressed on those four lines only, with this measurement as the
 * evidence — the same reconciliation series-tables.tsx records for its
 * scroll region. `rowgroup` on thead/tbody was NOT load-bearing in the
 * same measurement (rows attach to the table without it) and is omitted.
 */
import Link from 'next/link'
import styles from './systems-table.module.css'
import { identifierDefinitionBody } from '@/lib/identifiers'

const ROWS = [
  {
    code: 'FIN',
    name: 'Fridge.com Identification Number',
    kind: 'Identifier',
    names: identifierDefinitionBody('FIN'),
    format: 'FIN-0000000',
    // FRIDGE-868 — the catalog home. /products 308s onto /collections/all
    // (redirects.csv, the 07/11/2026 consolidation), so naming it here sent
    // the reader and the crawler through a redirect to reach the page this
    // row is about. Label and href move together: the label IS the path.
    home: { label: '/collections/all', href: '/collections/all' },
    page: '/about-us/fin',
  },
  {
    code: 'FPN',
    name: 'Fridge.com Part Number',
    kind: 'Identifier',
    names: identifierDefinitionBody('FPN'),
    format: 'FPN-0000000',
    home: { label: '/parts', href: '/parts' },
    page: '/about-us/fpn',
  },
  {
    code: 'FFN',
    name: 'Fridge.com Filter Number',
    kind: 'Identifier',
    names: identifierDefinitionBody('FFN'),
    format: 'FFN-0000000',
    home: { label: '/filters', href: '/filters' },
    page: '/about-us/ffn',
  },
  {
    code: 'FIS',
    name: 'Fridge.com Intelligence Score',
    kind: 'Score · 0–100',
    names: identifierDefinitionBody('FIS'),
    format: '0–100',
    home: { label: '/local', href: '/local' },
    page: '/about-us/fis',
  },
] as const

export function SystemsComparisonTable({ current }: { current?: 'FIN' | 'FPN' | 'FFN' | 'FIS' }) {
  return (
    <div className={`${styles.container} overflow-x-auto rounded-xl border border-gray-200`}>
      <table role="table" className={`${styles.table} w-full text-sm`}>
        <caption className="sr-only">
          The four Fridge.com systems — FIN, FPN, FFN, FIS — compared: what each is, what it names, its format, and where it lives.
        </caption>
        <thead>
          <tr role="row" className="bg-gray-50 text-left">
            <th role="columnheader" scope="col" className="px-4 py-2.5 text-[10px] font-bold uppercase tracking-[0.12em] text-gray-500">System</th>
            <th role="columnheader" scope="col" className="px-4 py-2.5 text-[10px] font-bold uppercase tracking-[0.12em] text-gray-500">Kind</th>
            <th role="columnheader" scope="col" className="px-4 py-2.5 text-[10px] font-bold uppercase tracking-[0.12em] text-gray-500">Definition</th>
            <th role="columnheader" scope="col" className="px-4 py-2.5 text-[10px] font-bold uppercase tracking-[0.12em] text-gray-500">Format</th>
            <th role="columnheader" scope="col" className="px-4 py-2.5 text-[10px] font-bold uppercase tracking-[0.12em] text-gray-500">Lives at</th>
          </tr>
        </thead>
        <tbody>
          {ROWS.map((r) => {
            const isCurrent = r.code === current
            return (
              <tr role="row" key={r.code} className={isCurrent ? 'bg-[#fff7f0]' : 'bg-white'}>
                <th role="rowheader" scope="row" className="px-4 py-3 border-t border-gray-100 whitespace-nowrap text-left font-normal">
                  {isCurrent ? (
                    <span className="font-mono text-[13px] font-bold text-[#b25410]">{r.code}</span>
                  ) : (
                    <Link href={r.page} className="font-mono text-[13px] font-bold text-[#0469f0] hover:underline">
                      {r.code}
                    </Link>
                  )}
                  <span className={`${styles.name} block text-[11px] text-gray-500`}>{r.name}</span>
                </th>
                {/* role="cell" is load-bearing under the phone CSS — see the
                    measurement in the file header. Suppressed per line. */}
                {/* eslint-disable-next-line jsx-a11y/no-interactive-element-to-noninteractive-role -- restates the native cell role that display:block strips; measured in the header note */}
                <td role="cell" className="px-4 py-3 border-t border-gray-100 whitespace-nowrap text-gray-700"><span aria-hidden="true" className={styles.label}>Kind: </span>{r.kind}</td>
                {/* eslint-disable-next-line jsx-a11y/no-interactive-element-to-noninteractive-role -- restates the native cell role that display:block strips; measured in the header note */}
                <td role="cell" className="px-4 py-3 border-t border-gray-100 text-gray-700">{r.names}</td>
                {/* eslint-disable-next-line jsx-a11y/no-interactive-element-to-noninteractive-role -- restates the native cell role that display:block strips; measured in the header note */}
                <td role="cell" className="px-4 py-3 border-t border-gray-100 whitespace-nowrap font-mono text-[12px] text-gray-700"><span aria-hidden="true" className={styles.label}>Format: </span>{r.format}</td>
                {/* eslint-disable-next-line jsx-a11y/no-interactive-element-to-noninteractive-role -- restates the native cell role that display:block strips; measured in the header note */}
                <td role="cell" className="px-4 py-3 border-t border-gray-100 whitespace-nowrap">
                  <span aria-hidden="true" className={styles.label}>Lives at: </span>
                  <Link href={r.home.href} className="text-[#0469f0] hover:underline">{r.home.label}</Link>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
